import { dateOf, daysBetween, type IsoDateTime } from "../engine/dates";

/**
 * Backup reminder and export delivery. The reminder is due when the last backup is more than REMINDER_DAYS old (or
 * there has never been one) and at least REMINDER_SHOTS shots have been logged since. It never pops up: the UI shows
 * a dot on the settings button and a row at the top of Settings.
 */
export const LAST_BACKUP_AT = "last_backup_at";
export const REMINDER_DAYS = 14;
export const REMINDER_SHOTS = 10;

export interface BackupReminder {
  readonly due: boolean;
  /** whole days since the last backup, or null when there has never been one */
  readonly daysAgo: number | null;
  readonly shotsSince: number;
}

export function backupReminder(lastBackupAt: IsoDateTime | null, now: IsoDateTime, shotsSince: number): BackupReminder {
  const daysAgo = lastBackupAt === null ? null : Math.max(0, daysBetween(dateOf(lastBackupAt), dateOf(now)));
  const old = daysAgo === null || daysAgo > REMINDER_DAYS;
  return { due: old && shotsSince >= REMINDER_SHOTS, daysAgo, shotsSince };
}

/** "Back up now — last backup 16 days ago" / "— no backup yet". */
export function reminderText(r: BackupReminder): string {
  if (r.daysAgo === null) return "Back up now — no backup yet";
  return `Back up now — last backup ${r.daysAgo} ${r.daysAgo === 1 ? "day" : "days"} ago`;
}

export function backupFilename(now: IsoDateTime): string {
  return `pucktech-backup-${dateOf(now)}.json`;
}

export type DeliveryResult = "shared" | "downloaded" | "cancelled";

/** The browser pieces delivery needs, injectable for tests. */
export interface DeliveryEnv {
  canShare?: (data: { files: File[] }) => boolean;
  share?: (data: { files: File[]; title?: string }) => Promise<void>;
  download: (file: File) => void;
}

/**
 * Hand the backup to the iOS share sheet (Save to Files / iCloud Drive / Mail) when the browser can share files,
 * else download it. A user who dismisses the share sheet gets "cancelled" and the backup is not recorded; any other
 * share failure falls back to a download.
 */
export async function deliverBackup(file: File, env: DeliveryEnv): Promise<DeliveryResult> {
  if (env.share && env.canShare?.({ files: [file] })) {
    try {
      await env.share({ files: [file], title: "Puck Tech backup" });
      return "shared";
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return "cancelled";
    }
  }
  env.download(file);
  return "downloaded";
}

/** The real browser environment. */
export function browserDeliveryEnv(): DeliveryEnv {
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  const env: DeliveryEnv = {
    download: (file) => {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
  };
  if (typeof nav.share === "function" && typeof nav.canShare === "function") {
    env.share = (d) => nav.share(d);
    env.canShare = (d) => (nav.canShare as (d: { files: File[] }) => boolean)(d);
  }
  return env;
}
